/**
 * Where the title's letters stand: the row, read off the hidden DOM title, that both the 3D
 * letters and the loading stand-in are laid out in. No Three.js here: the page's entry chunk
 * needs this, and must not pull the renderer in with it.
 */
import { BASELINE, CAP_TOP, type LetterBox, type Viewport } from './letterPegs.ts'
import titleModel from './titleModel.json' with { type: 'json' }

export interface LetterPlacement {
  char: string
  /** World x of the glyph's centre. */
  x: number
  /** World y of the baseline. */
  baseline: number
  capHeight: number
}

/** Where each letter of the measured DOM title sits in the world, so the 3D glyphs replace it in place. */
export function placeLetters(letters: LetterBox[], view: Viewport): LetterPlacement[] {
  const pxToWorld = view.wWidth / view.width
  const toWorldX = (px: number) => (px / view.width - 0.5) * view.wWidth
  const toWorldY = (px: number) => (0.5 - px / view.height) * view.wHeight
  const out: LetterPlacement[] = []
  for (const letter of letters) {
    if (letter.width <= 0 || letter.char.trim() === '') continue
    out.push({
      char: letter.char.toUpperCase(),
      x: toWorldX(letter.x + letter.width / 2),
      baseline: toWorldY(letter.y + letter.height * BASELINE),
      capHeight: letter.height * (BASELINE - CAP_TOP) * pxToWorld,
    })
  }
  return out
}

/**
 * Fit a ready-made letter mesh (one glyph of the title model) to a cap height: uniform scale,
 * standing on y = 0, centred on x and z. Returns a new geometry; the source is left untouched.
 */

/**
 * Lay a row of letters out like the DOM title's flex row with `justify-between`: the letters
 * keep their own widths, the leftover space is shared evenly between them (a word break gets
 * extra), and only if they would not fit even packed tight does the row shrink.
 */
export function layoutRow(
  widths: number[],
  rowWidth: number,
  capHeight: number,
  options: {
    minGapRatio?: number
    maxGapRatio?: number
    maxFill?: number
    wordBreaksAfter?: number[]
    wordGapRatio?: number
  } = {}
): { x: number; scaleToFit: number }[] {
  const { minGapRatio = 0.06, maxGapRatio = Infinity, maxFill = 1, wordBreaksAfter = [], wordGapRatio = 0.5 } = options
  const n = widths.length
  const minGap = capHeight * minGapRatio
  const maxGap = capHeight * maxGapRatio
  const wordExtra = capHeight * wordGapRatio
  const extraAfter = (i: number) => (i < n - 1 && wordBreaksAfter.includes(i) ? wordExtra : 0)
  const lettersWidth = widths.reduce((a, b) => a + b, 0)
  const words = wordBreaksAfter.filter((i) => i < n - 1).length
  const fixed = lettersWidth + minGap * (n - 1) + words * wordExtra
  let scaleToFit = Math.min(1, rowWidth / fixed)
  // Narrow letters that cannot reach the row's edges even at the widest gap grow instead, up to the allowed fill.
  const widest = lettersWidth + maxGap * (n - 1) + words * wordExtra
  if (scaleToFit === 1 && Number.isFinite(widest) && widest < rowWidth)
    scaleToFit = Math.min(maxFill, rowWidth / widest)
  const spare = Math.max(0, rowWidth - fixed * scaleToFit)
  // Letters spread to fill the row, but never further apart than the maximum gap: a row of
  // narrow letters stays a word, centred, rather than scattering across the width.
  const gap = Math.min(maxGap * scaleToFit, minGap * scaleToFit + (n > 1 ? spare / (n - 1) : 0))
  const total =
    lettersWidth * scaleToFit + gap * (n - 1) + wordBreaksAfter.filter((i) => i < n - 1).length * wordExtra * scaleToFit
  let cursor = -total / 2
  return widths.map((w, i) => {
    const x = cursor + (w * scaleToFit) / 2
    cursor += w * scaleToFit + gap + extraAfter(i) * scaleToFit
    return { x, scaleToFit }
  })
}

/** How the title row is set: shared by the 3D letters and by the DOM title that stands in for them while they load. */
export const TITLE_LAYOUT = {
  /** The model's letters are bolder and wider than the DOM title's, so they stand a little shorter to keep the row airy. */
  capScale: 0.95,
  /** Letters never sit further apart than this many cap heights, however wide the screen. */
  maxGapRatio: 0.22,
  /** Narrow letters grow up to this much past the DOM title's size to fill its width. */
  maxFill: 1.35,
}

/** Width over cap height of each letter of the title model (scripts/models/title.gltf, served compressed as src/assets/title.glb); a test keeps this true to the file. */
export const MODEL_LETTER_RATIOS: Record<string, number> = titleModel.ratios

export interface TitleFrame {
  placements: LetterPlacement[]
  /** Cap height the 3D letters are built at, in world units. */
  cap: number
  /** Baseline the 3D letters stand on before the row's fill scales them. */
  baseline: number
  rowWidth: number
  centre: number
  /** Indices of the letters a word ends after. */
  breaks: number[]
}

/** The row the title is set in, read off the DOM letters: its width, centre, cap height and word breaks. */
export function titleFrame(letters: LetterBox[], view: Viewport): TitleFrame | null {
  const placements = placeLetters(letters, view)
  if (placements.length === 0) return null
  const cap = placements[0].capHeight * TITLE_LAYOUT.capScale
  const baseline = placements[0].baseline + (placements[0].capHeight - cap) / 2
  const left = placements[0].x
  const right = placements[placements.length - 1].x
  const slot = (right - left) / Math.max(1, placements.length - 1)
  const breaks: number[] = []
  let seen = 0
  for (const letter of letters) {
    if (letter.char.trim() === '') breaks.push(seen - 1)
    else seen++
  }
  return { placements, cap, baseline, rowWidth: right - left + slot, centre: (left + right) / 2, breaks }
}

export interface TitleBox {
  /** Centre of the letter. */
  x: number
  centreY: number
  width: number
  height: number
}

/** Where each letter of the row stands, given the letters' widths at the frame's cap height. */
export function titleBoxes(
  frame: TitleFrame,
  widths: number[]
): { boxes: TitleBox[]; row: { x: number; scaleToFit: number }[] } {
  const row = layoutRow(widths, frame.rowWidth, frame.cap, {
    wordBreaksAfter: frame.breaks,
    maxGapRatio: TITLE_LAYOUT.maxGapRatio,
    maxFill: TITLE_LAYOUT.maxFill,
  })
  const boxes = row.map((r, i) => ({
    x: frame.centre + r.x,
    centreY: frame.baseline + (frame.cap * r.scaleToFit) / 2,
    width: widths[i] * r.scaleToFit,
    height: frame.cap * r.scaleToFit,
  }))
  return { boxes, row }
}

/**
 * The boxes, in CSS pixels, the DOM title's letters must fill to stand exactly where the 3D
 * letters will: the same row rule, run with a viewport whose world unit is the pixel.
 */
export function titleBoxesPx(
  letters: LetterBox[],
  width: number,
  height: number
): { left: number; top: number; width: number; height: number }[] | null {
  const frame = titleFrame(letters, { width, height, wWidth: width, wHeight: height })
  if (!frame) return null
  const widths = frame.placements.map((p) => (MODEL_LETTER_RATIOS[p.char] ?? 0.5) * frame.cap)
  return titleBoxes(frame, widths).boxes.map((b) => ({
    left: b.x + width / 2 - b.width / 2,
    top: height / 2 - b.centreY - b.height / 2,
    width: b.width,
    height: b.height,
  }))
}
